import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

import { DateTime } from "luxon";

export interface DateRange {
    from: Date;
    to: Date;
}

export interface TemporalLookupInfo {
    lookup: string;
    value: any;
}

export interface RelativeDateResult {
    range?: DateRange;
    temporal?: TemporalLookupInfo;
}

/**
 * Parse une expression de date relative en anglais et retourne une plage de dates
 * @param input - L'expression en anglais (ex: "today", "yesterday", "last 7 days", etc.)
 * @returns DateRange ou null si l'expression n'est pas reconnue
 */
export function parseRelativeDate(input: string): DateRange | null {
    const result = parseRelativeDateWithTemporal(input);
    return result?.range || null;
}

/**
 * Parse une expression de date relative en anglais et retourne soit une plage de dates soit des informations de lookup temporel
 * @param input - L'expression en anglais (ex: "today", "yesterday", "last 7 days", etc.)
 * @returns RelativeDateResult avec range et/ou temporal, ou null si l'expression n'est pas reconnue
 */
export function parseRelativeDateWithTemporal(input: string): RelativeDateResult | null {
    const now = DateTime.now();
    const normalizedInput = input.toLowerCase().trim();

    // Expressions simples qui peuvent utiliser des lookups Django spécifiques
    switch (normalizedInput) {
        case "today":
            return {
                range: {
                    from: now.startOf("day").toJSDate(),
                    to: now.endOf("day").toJSDate()
                },
                temporal: { lookup: "__date", value: now.toISODate() }
            };

        case "yesterday":
            const yesterday = now.minus({ days: 1 });
            return {
                range: {
                    from: yesterday.startOf("day").toJSDate(),
                    to: yesterday.endOf("day").toJSDate()
                },
                temporal: { lookup: "__date", value: yesterday.toISODate() }
            };

        case "this week":
            return {
                range: {
                    from: now.startOf("week").toJSDate(),
                    to: now.endOf("week").toJSDate()
                },
                temporal: { lookup: "__week", value: now.weekNumber }
            };

        case "last week":
            const lastWeek = now.minus({ weeks: 1 });
            return {
                range: {
                    from: lastWeek.startOf("week").toJSDate(),
                    to: lastWeek.endOf("week").toJSDate()
                },
                temporal: { lookup: "__week", value: lastWeek.weekNumber }
            };

        case "this month":
            return {
                range: {
                    from: now.startOf("month").toJSDate(),
                    to: now.endOf("month").toJSDate()
                },
                temporal: { lookup: "__month", value: now.month }
            };

        case "last month":
            const lastMonth = now.minus({ months: 1 });
            return {
                range: {
                    from: lastMonth.startOf("month").toJSDate(),
                    to: lastMonth.endOf("month").toJSDate()
                },
                temporal: { lookup: "__month", value: lastMonth.month }
            };

        case "next month":
            const nextMonth = now.plus({ months: 1 });
            return {
                range: {
                    from: nextMonth.startOf("month").toJSDate(),
                    to: nextMonth.endOf("month").toJSDate()
                },
                // Utilisation de __year_month pour gérer le changement d'année (ex. décembre → janvier)
                temporal: { lookup: "__year_month", value: { year: nextMonth.year, month: nextMonth.month } }
            };

        case "this year":
            return {
                range: {
                    from: now.startOf("year").toJSDate(),
                    to: now.endOf("year").toJSDate()
                },
                temporal: { lookup: "__year", value: now.year }
            };

        case "last year":
            const lastYear = now.minus({ years: 1 });
            return {
                range: {
                    from: lastYear.startOf("year").toJSDate(),
                    to: lastYear.endOf("year").toJSDate()
                },
                temporal: { lookup: "__year", value: lastYear.year }
            };
    }

    // Vérifier les formats spéciaux comme "2023" (année) ou "2023-03" (année-mois)
    if (/^\d{4}$/.test(normalizedInput)) {
        const year = parseInt(normalizedInput);
        const yearStart = DateTime.fromObject({ year });
        return {
            range: {
                from: yearStart.startOf("year").toJSDate(),
                to: yearStart.endOf("year").toJSDate()
            },
            temporal: { lookup: "__year", value: year }
        };
    }

    if (/^\d{4}-\d{2}$/.test(normalizedInput)) {
        const [yearStr, monthStr] = normalizedInput.split("-");
        const year = parseInt(yearStr);
        const month = parseInt(monthStr);
        const monthStart = DateTime.fromObject({ year, month });
        return {
            range: {
                from: monthStart.startOf("month").toJSDate(),
                to: monthStart.endOf("month").toJSDate()
            },
            temporal: { lookup: "__year_month", value: { year, month } }
        };
    }

    // Expressions avec nombres (ex: "last 7 days", "2 months ago") - ces expressions restent des plages
    const patterns = [
        // "last X days/weeks/months/years"
        {
            regex: /^last (\d+) (days?|weeks?|months?|years?)$/,
            handler: (match: RegExpMatchArray) => {
                const amount = parseInt(match[1]);
                const unit = match[2].replace(/s$/, "") as "day" | "week" | "month" | "year"; // Remove 's' if present
                const startDate = now.minus({ [unit + "s"]: amount }).startOf("day");
                return {
                    range: {
                        from: startDate.toJSDate(),
                        to: now.endOf("day").toJSDate()
                    }
                };
            }
        },
        // "X days/weeks/months/years ago"
        {
            regex: /^(\d+) (days?|weeks?|months?|years?) ago$/,
            handler: (match: RegExpMatchArray) => {
                const amount = parseInt(match[1]);
                const unit = match[2].replace(/s$/, "") as "day" | "week" | "month" | "year";
                const targetDate = now.minus({ [unit + "s"]: amount });

                // Pour les unités plus grandes, préférer des lookups temporels spécifiques
                if (unit === "year") {
                    return {
                        range: {
                            from: targetDate.startOf("year").toJSDate(),
                            to: targetDate.endOf("year").toJSDate()
                        },
                        temporal: { lookup: "__year", value: targetDate.year }
                    };
                }

                if (unit === "month") {
                    // Pour "X months ago", retourner le premier jour du mois en question
                    return {
                        range: {
                            from: targetDate.startOf("month").toJSDate(),
                            to: targetDate.endOf("month").toJSDate()
                        },
                        temporal: { lookup: "__year_month", value: { year: targetDate.year, month: targetDate.month } }
                    };
                }

                if (unit === "week") {
                    return {
                        range: {
                            from: targetDate.startOf("week").toJSDate(),
                            to: targetDate.endOf("week").toJSDate()
                        },
                        temporal: { lookup: "__week", value: targetDate.weekNumber }
                    };
                }

                // Pour les jours, utiliser la plage de dates standard
                return {
                    range: {
                        from: targetDate.startOf("day").toJSDate(),
                        to: targetDate.endOf("day").toJSDate()
                    }
                };
            }
        },
        // "next X days/weeks/months/years"
        {
            regex: /^next (\d+) (days?|weeks?|months?|years?)$/,
            handler: (match: RegExpMatchArray) => {
                const amount = parseInt(match[1]);
                const unit = match[2].replace(/s$/, "") as "day" | "week" | "month" | "year";
                const endDate = now.plus({ [unit + "s"]: amount }).endOf("day");
                return {
                    range: {
                        from: now.startOf("day").toJSDate(),
                        to: endDate.toJSDate()
                    }
                };
            }
        },
        // A single day, the future counterpart of "7 days ago". "next 7 days" is the
        // window from today; "in 7 days" is only the day 7 days ahead.
        {
            regex: /^in (\d+) (days?|weeks?|months?|years?)$/,
            handler: (match: RegExpMatchArray) => {
                const amount = parseInt(match[1]);
                const unit = match[2].replace(/s$/, "") as "day" | "week" | "month" | "year";
                const target = now.plus({ [unit + "s"]: amount });
                return {
                    range: {
                        from: target.startOf("day").toJSDate(),
                        to: target.endOf("day").toJSDate()
                    }
                };
            }
        },
        {
            regex: /^back (\d+) (days?|weeks?|months?|years?)$/,
            handler: (match: RegExpMatchArray) => {
                const amount = parseInt(match[1]);
                const unit = match[2].replace(/s$/, "") as "day" | "week" | "month" | "year";
                const target = now.minus({ [unit + "s"]: amount });
                return {
                    range: {
                        from: target.startOf("day").toJSDate(),
                        to: target.endOf("day").toJSDate()
                    }
                };
            }
        }
    ];

    // Tester les patterns
    for (const pattern of patterns) {
        const match = normalizedInput.match(pattern.regex);
        if (match) {
            try {
                return pattern.handler(match);
            } catch (error) {
                console.error("Error parsing relative date:", error);
                return null;
            }
        }
    }

    return null;
}

/**
 * Obtient une liste d'exemples de dates relatives couramment utilisées
 */
export function getRelativeDateExamples(): string[] {
    return [
        "today",
        "yesterday",
        "last 7 days",
        "last 30 days",
        "next 7 days",
        "in 7 days",
        "next 10 days",
        "this week",
        "last week",
        "this month",
        "last month",
        "next month",
        "last 3 months",
        "this year",
        "last year",
        "2 days ago",
        "1 week ago",
        "6 months ago"
    ];
}

export function stringToColor(string: string) {
    // Liste des couleurs de la palette Tailwind
    const tailwindColors = ["mastic-grey", "azure-blue", "denim-blue", "straw-yellow", "raw-umber-marron", "shamrock-green", "ut-orange", "pantone-red"];

    // Utiliser la somme des codes ASCII pour sélectionner une couleur de manière déterministe
    const sum = string
        .split("")
        .map(char => char.charCodeAt(0))
        .reduce((a, b) => a + b, 0);

    // Sélectionner une couleur de la palette
    const selectedColor = tailwindColors[sum % tailwindColors.length];

    // Déterminer la variante de luminosité (100, 80, ou 60) de manière déterministe
    const brightnessVariant = [100, 85, 70][sum % 3];

    // Retourner la couleur au format HSL avec la variante de luminosité
    return `hsl(var(--${selectedColor}-hsl) / ${brightnessVariant}%)`;
}

function shortDateTime(date: Date) {
    return DateTime.fromJSDate(date).setLocale("en-GB").setZone("Europe/Paris").toFormat("d MMM yy, HH:mm");
}

function shortDate(date: Date) {
    return DateTime.fromJSDate(date).setLocale("en-GB").setZone("Europe/Paris").toFormat("d MMM yy");
}

function longDateTime(date: Date) {
    return DateTime.fromJSDate(date).setLocale("en-GB").setZone("Europe/Paris").toFormat("d MMMM yyyy 'at' HH:mm");
}

function longDate(date: Date) {
    return DateTime.fromJSDate(date).setLocale("en-GB").setZone("Europe/Paris").toFormat("d MMMM yyyy");
}

function extraLongDateTime(date: Date) {
    return DateTime.fromJSDate(date).setLocale("en-GB").setZone("Europe/Paris").toFormat("EEEE, d MMMM yyyy 'at' HH:mm");
}

function dateTimestamp(date: Date) {
    return DateTime.fromJSDate(date).setLocale("en-GB").setZone("Europe/Paris").toFormat("dd/MM/yyyy 'at' HH:mm:ss");
}

function extraLongDate(date: Date) {
    return DateTime.fromJSDate(date).setLocale("en-GB").setZone("Europe/Paris").toFormat("EEEE, d MMMM yyyy");
}

// Fonction pour détecter et formater automatiquement les dates
export function formatDateValue(value: any, layout: "long" | "short" | "extra-long" | "timestamp"): any {
    if (!value) return value;

    // Détecter si c'est une date
    let isDate = false;
    let isDateTime = false;

    // Vérifier si c'est une instance DateTime Luxon
    if (typeof DateTime !== "undefined" && DateTime.isDateTime && DateTime.isDateTime(value)) {
        isDate = true;
        isDateTime = value.hour !== 0 || value.minute !== 0 || value.second !== 0 || value.millisecond !== 0;
    }
    // Vérifier si c'est une instance Date
    else if (value instanceof Date) {
        isDate = true;
        isDateTime = value.getHours() !== 0 || value.getMinutes() !== 0 || value.getSeconds() !== 0 || value.getMilliseconds() !== 0;
    }
    // Vérifier si c'est une chaîne ISO date
    else if (typeof value === "string") {
        // Patterns pour détecter les dates
        const dateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
        const datePattern = /^\d{4}-\d{2}-\d{2}$/;

        if (dateTimePattern.test(value)) {
            isDate = true;
            isDateTime = true;
        } else if (datePattern.test(value)) {
            isDate = true;
            isDateTime = false;
        }
    }

    // Si c'est une date, la formater selon le layout
    if (isDate) {
        let dateValue: Date;

        // Si c'est un objet DateTime Luxon, le convertir en Date
        if (typeof DateTime !== "undefined" && DateTime.isDateTime && DateTime.isDateTime(value)) {
            dateValue = value.toJSDate();
        } else {
            dateValue = value instanceof Date ? value : new Date(value);
        }

        if (layout === "short") {
            return isDateTime ? shortDateTime(dateValue) : shortDate(dateValue);
        } else if (layout === "long") {
            return isDateTime ? longDateTime(dateValue) : longDate(dateValue);
        } else if (layout === "extra-long") {
            return isDateTime ? extraLongDateTime(dateValue) : extraLongDate(dateValue);
        } else if (layout === "timestamp") {
            return isDateTime ? dateTimestamp(dateValue) : longDate(dateValue);
        }
    }

    return value;
}

export function formatCurrencyValue(value: any): any {
    if (typeof value === "number") {
        return value.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
    } else if (typeof value === "string") {
        return Number(value).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
    }
    return value;
}

/**
 * Arrondit une date/heure à l'intervalle de 15 minutes le plus proche
 * @param date - La date/heure à arrondir
 * @returns La date/heure arrondie à 15 minutes près
 */
export function roundToNearest15Minutes(date: DateTime): DateTime {
    const minutes = date.minute;
    const roundedMinutes = Math.round(minutes / 15) * 15;

    if (roundedMinutes === 60) {
        return date.plus({ hours: 1 }).set({ minute: 0, second: 0, millisecond: 0 });
    } else {
        return date.set({ minute: roundedMinutes, second: 0, millisecond: 0 });
    }
}

export function normalizeStreet(value: string | null | undefined): string {
    if (value == null || typeof value !== "string") return "";
    let sanitized = value
        .replace(/^[^a-zA-Z0-9]+/, "")  // strip leading non-alphanumeric
        .replace(/["«»]/g, "")          // optional: remove typographic quotes
    // empty or no alphanumeric left (e.g. "?", "…") → treat as empty
    if (!sanitized || !/[a-zA-Z0-9]/.test(sanitized)) return "";
    return sanitized;
}

/**
 * Height of the fixed bottom UtilityBar, which overlays the viewport on pages that do not
 * reserve space for it. Floating elements must treat it as unavailable space so they do not
 * end up underneath it.
 */
export function getViewportBottomInset(): number {
    if (typeof document === "undefined") return 0;
    const utilityBar = document.querySelector("[data-utility-bar]");
    return utilityBar ? utilityBar.getBoundingClientRect().height : 0;
}

/**
 * Detects if an error should be ignored (not reported to Bugsnag).
 * Includes chunk load errors, browser false positives (e.g. ResizeObserver), and React Router deferred abort noise.
 */
export function shouldIgnoreError(error: unknown): boolean {
    const message =
        error instanceof Error
            ? error.message
            : typeof error === "object" && error !== null && "errorMessage" in error
                ? String((error as { errorMessage?: string }).errorMessage ?? "")
                : typeof error === "string"
                    ? error
                    : String(error ?? "");

    const chunkErrors = [
        "Failed to fetch",
        "Failed to fetch dynamically imported module",
        "Loading chunk",
        "Loading CSS chunk",
        "Importing a module script failed",
    ];

    const authErrors = [
        "Session expirée, veuillez vous reconnecter",
        "No active account found with the given credentials",
        '{"detail":"You do not have permission to perform this action."}'
    ];

    const browserFalsePositives = [
        "ResizeObserver loop completed with undelivered notifications"
    ];

    /** React Router: deferred loader promise aborted on navigation — not an app bug */
    const routerNonActionable = ["Deferred data aborted"];

    const isAuthError = authErrors.some(error => message.includes(error));
    const isChunkError = chunkErrors.some(error => message.includes(error));
    const isBrowserFalsePositive = browserFalsePositives.some(error => message.includes(error));
    const isRouterNoise = routerNonActionable.some(s => message.includes(s));

    if (isChunkError) {
        localStorage.setItem('chunk-error', 'true');
        window.dispatchEvent(new CustomEvent('chunk-error-detected'));
    }

    return isAuthError || isChunkError || isBrowserFalsePositive || isRouterNoise;
}
